import { calculateItemStatus } from './patternEngine'

// ─── Types ────────────────────────────────────────────────────────────────────

type PantryItem = {
  id: string
  name: string
  emoji: string
  display_category: string
  avg_restock_cycle_days: number | null
  last_confirmed_at: string | null
  confirmation_count: number
  confidence_score: number
  is_priority: boolean
  is_one_off: boolean
  status_override?: string | null
}

type ListCandidate = {
  item_id: string
  name: string
  emoji: string
  display_category: string
  status: string
  urgencyScore: number
  confidenceScore: number
  isOneOff: boolean
}

// ─── generateShoppingList ─────────────────────────────────────────────────────

export function generateShoppingList(pantryItems: PantryItem[]): ListCandidate[] {
  const candidates: ListCandidate[] = []

  for (const item of pantryItems) {
    // Convert the stored ISO string to a Date object, or null if never confirmed
    const lastConfirmedAt = item.last_confirmed_at ? new Date(item.last_confirmed_at) : null

    // Run the pattern engine to get status, depletion fraction, and days remaining
    const { status, depletion, daysRemaining } = calculateItemStatus(
      lastConfirmedAt,
      item.avg_restock_cycle_days,
      item.confirmation_count,
      item.status_override,
    )

    // Blend depletion (70%) with confidence (30%) to produce a raw urgency score
    const rawUrgency = (depletion * 0.7) + (item.confidence_score * 0.3)

    // Determine whether this item qualifies for the shopping list
    const isRedStatus   = status === 'red'
    const isUsed        = status === 'used'
    const isAmberSoon   = status === 'amber' && daysRemaining <= 3
    const isPriority    = item.is_priority
    const isOneOff      = item.is_one_off
    const shouldInclude = isRedStatus || isUsed || isAmberSoon || isPriority

    if (!shouldInclude) continue

    // Priority items are pinned to the top; used items rank just below priority
    const urgencyScore = isPriority ? 999 : isUsed ? 800 : rawUrgency

    candidates.push({
      item_id:         item.id,
      name:            item.name,
      emoji:           item.emoji,
      display_category: item.display_category,
      status,
      urgencyScore,
      confidenceScore: item.confidence_score,
      isOneOff,
    })
  }

  // Sort: one-off items sink to the bottom; everything else descends by urgency
  candidates.sort((a, b) => {
    if (a.isOneOff && !b.isOneOff) return  1  // a sinks
    if (!a.isOneOff && b.isOneOff) return -1  // b sinks
    return b.urgencyScore - a.urgencyScore     // higher urgency first
  })

  return candidates
}

// ─── Dev-only self-tests ──────────────────────────────────────────────────────

if (import.meta.env.DEV) {
  // Helper: return an ISO string N days ago
  const isoAgo = (days: number) => new Date(Date.now() - days * 86_400_000).toISOString()

  // Shared base fields to avoid repetition in each test item
  const base = {
    display_category:       'Test',
    avg_restock_cycle_days: 10,
    confirmation_count:     5,
    confidence_score:       0.8,
    is_priority:            false,
    is_one_off:             false,
  }

  // Test 1 — red item (11 days into a 10-day cycle) must appear in the list
  const redItem: PantryItem = {
    ...base,
    id:                'item-red',
    name:              'Red Item',
    emoji:             '🔴',
    last_confirmed_at: isoAgo(11),
  }

  // Test 2 — green item (2 days into a 10-day cycle), no priority — must be excluded
  const greenItem: PantryItem = {
    ...base,
    id:                'item-green',
    name:              'Green Item',
    emoji:             '🟢',
    last_confirmed_at: isoAgo(2),
  }

  // Test 3 — green item but flagged is_priority — must always appear
  const priorityItem: PantryItem = {
    ...base,
    id:                'item-priority',
    name:              'Priority Item',
    emoji:             '⭐',
    last_confirmed_at: isoAgo(2),
    is_priority:       true,
  }

  // Test 4 — multiple items to verify urgency sort order
  const amberItem: PantryItem = {
    ...base,
    id:                'item-amber',
    name:              'Amber Item',
    emoji:             '🟡',
    last_confirmed_at: isoAgo(8), // 80% depleted — amber
    confidence_score:  0.5,
  }

  const list = generateShoppingList([redItem, greenItem, priorityItem, amberItem])

  // Verify red item is present
  const hasRed      = list.some(c => c.item_id === 'item-red')
  // Verify green non-priority item is absent
  const hasGreen    = list.some(c => c.item_id === 'item-green')
  // Verify priority item is present despite being green
  const hasPriority = list.some(c => c.item_id === 'item-priority')
  // Verify priority item sits at urgencyScore 999 (top of list)
  const priorityFirst = list[0]?.item_id === 'item-priority'

  console.log('[listGenerator] red item included (expect true):', hasRed)
  console.log('[listGenerator] green item excluded (expect false):', hasGreen)
  console.log('[listGenerator] priority item included (expect true):', hasPriority)
  console.log('[listGenerator] priority item is first (expect true):', priorityFirst)
  console.log('[listGenerator] full sorted list:', list.map(c => `${c.name} (urgency: ${c.urgencyScore.toFixed(3)})`))
}
